import { z } from "zod";

export const recipeTypeSchema = z.enum([
  "ice_cream",
  "gelato",
  "sherbet",
  "sorbet",
  "frozen_yogurt",
  "ganache",
  "composite",
  "other",
]);

export const recipeNameSchema = z
  .string()
  .min(1, "Recipe name is required")
  .max(100, "Recipe name must be less than 100 characters");

export const recipeDescriptionSchema = z
  .string()
  .max(5000, "Description must be less than 5000 characters");

export const recipeInstructionsSchema = z
  .string()
  .max(5000, "Instructions must be less than 5000 characters");

export const recipeIngredientNotesValueSchema = z
  .string()
  .max(100, "Ingredient notes must be at most 100 characters");

export const recipeIngredientNotesSchema =
  recipeIngredientNotesValueSchema.optional();

export const MAX_RECIPE_INGREDIENTS = 50;
export const maxRecipeIngredientsMessage = `Recipes can have at most ${MAX_RECIPE_INGREDIENTS} ingredients`;

export const recipeIngredientAmountSchema = z.number().finite().min(0);
export const evaporationFractionSchema = z.number().min(0).max(1);
export const componentRoleSchema = z.enum(["base", "mix_in", "topping"]);
export const componentQuantityModeSchema = z.enum(["grams", "batches"]);
export const componentQuantitySchema = z.number().finite().positive();

export const recipeIngredientModifierSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("infusion") }).strict(),
  z
    .object({
      type: z.literal("evaporation"),
      percentage: evaporationFractionSchema,
    })
    .strict(),
]);

export const recipeIngredientModifiersSchema = z
  .array(recipeIngredientModifierSchema)
  .superRefine((modifiers, context) => {
    const modifierTypes = modifiers.map(({ type }) => type);
    if (new Set(modifierTypes).size !== modifierTypes.length) {
      context.addIssue({
        code: "custom",
        message: "Ingredient modifiers must be unique",
      });
    }
    if (
      modifierTypes.includes("infusion") &&
      modifierTypes.includes("evaporation")
    ) {
      context.addIssue({
        code: "custom",
        message: "Infusion ingredients cannot also use evaporation",
      });
    }
  });

// Persistence uses zero to represent no evaporation. Form and tool adapters
// retain explicit modifier objects, where even a zero-valued modifier exists.
export const persistedRecipeIngredientModifierFieldsSchema = z
  .object({
    isInfusion: z.boolean().default(false),
    evaporationPercentage: evaporationFractionSchema.default(0),
  })
  .superRefine((ingredient, context) => {
    const modifiers: RecipeIngredientModifier[] = [];
    if (ingredient.isInfusion) modifiers.push({ type: "infusion" });
    if (ingredient.evaporationPercentage > 0) {
      modifiers.push({
        type: "evaporation",
        percentage: ingredient.evaporationPercentage,
      });
    }
    const result = recipeIngredientModifiersSchema.safeParse(modifiers);
    if (!result.success) {
      for (const issue of result.error.issues) {
        context.addIssue({
          ...issue,
          path: ["evaporationPercentage"],
        });
      }
    }
  });

export type RecipeType = z.infer<typeof recipeTypeSchema>;
export type ComponentRole = z.infer<typeof componentRoleSchema>;
export type ComponentQuantityMode = z.infer<typeof componentQuantityModeSchema>;
export type RecipeIngredientModifier = z.infer<
  typeof recipeIngredientModifierSchema
>;
